// The web app's root: React Query, sign-in (Keycloak), the linked-account check and the routes (Plan, Library,
// Maps, Activities, shared routes and invites, sign-in callbacks). Pages load on demand.
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router';
import { AuthProvider } from '@/auth/AuthProvider';
import { LinkedAccountGate } from '@/auth/LinkedAccountGate';
import { SignInCallback } from '@/auth/SignInCallback';
import { rsvpToken } from '@/features/activities/rsvpToken';
import { sharedToken } from '@/features/share/share';
import { AppShell } from './AppShell';

// Pages load on demand; Plan and Maps pull in OpenLayers, Library doesn't need it.
const PlanPage = lazy(() => import('@/features/plan/PlanPage'));
const LibraryPage = lazy(() => import('@/features/library/LibraryPage'));
const MapsPage = lazy(() => import('@/features/maps/MapsPage'));
const GarminCallback = lazy(() => import('@/features/garmin/GarminCallback'));
const ConnectionCallback = lazy(() => import('@/features/connections/ConnectionCallback'));
const SharedRoutePage = lazy(() => import('@/features/share/SharedRoutePage'));
const ActivitiesPage = lazy(() => import('@/features/activities/ActivitiesPage'));
const RsvpPage = lazy(() => import('@/features/activities/RsvpPage'));

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, retry: 1 } },
});

const routes = [
  {
    element: <AppShell />,
    children: [
      { index: true, element: <Navigate to="/plan" replace /> },
      { path: 'plan', element: <PlanPage /> },
      { path: 'library', element: <LibraryPage /> },
      { path: 'maps', element: <MapsPage /> },
      { path: 'activities', element: <ActivitiesPage /> },
      { path: 'activities/:guid', element: <ActivitiesPage /> },
      // The OIDC callback lands here; AuthProvider completes the sign-in, then this moves on.
      { path: 'auth/callback', element: <SignInCallback /> },
      // Garmin Connect returns here after the user approves the link.
      { path: 'garmin/callback', element: <GarminCallback /> },
      // Strava (and other connected accounts) return here after the user approves the link.
      { path: 'connect/:provider/callback', element: <ConnectionCallback /> },
      { path: '*', element: <Navigate to="/plan" replace /> },
    ],
  },
];

const router = createBrowserRouter(routes);

export function App() {
  // Replying from an invite email: no sign-in.
  const rsvp = rsvpToken(window.location.pathname);
  if (rsvp) {
    return (
      <QueryClientProvider client={queryClient}>
        <Suspense fallback={null}>
          <RsvpPage token={rsvp} />
        </Suspense>
      </QueryClientProvider>
    );
  }
  const shared = sharedToken(window.location.pathname);
  if (shared) {
    return (
      <QueryClientProvider client={queryClient}>
        <Suspense fallback={null}>
          <SharedRoutePage token={shared} />
        </Suspense>
      </QueryClientProvider>
    );
  }
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <LinkedAccountGate>
          <Suspense fallback={null}>
            <RouterProvider router={router} />
          </Suspense>
        </LinkedAccountGate>
      </AuthProvider>
    </QueryClientProvider>
  );
}
